import React, { lazy } from 'react'
import ComponentLoader from './component-loader'
import AdminLayout from 'screens/admin/AdminLayout'
import { Navigate } from 'react-router-dom'
import { useLocationInfo } from 'context/location'
import { useAuth } from 'context/auth'

// TASK-056: the GoHighLevel field-mapping and integration screens were removed.

// DASHBOARD
const Dashboard = ComponentLoader(
  lazy(() => import('screens/admin/Dashboard/dashboard'))
)
const Home = ComponentLoader(lazy(() => import('screens/admin/home/home')))

// CAMPAIGNS
const CampaignModule = ComponentLoader(
  lazy(() => import('screens/admin/Campaigns/'))
)
const CampaignList = ComponentLoader(
  lazy(() => import('screens/admin/Campaigns/campaign-list'))
)
const CampaignInsight = ComponentLoader(
  lazy(() => import('screens/admin/Campaigns/insight/'))
)
// Campaign Studio v2 (TASK-064). Reachable by URL until TASK-069 switches
// "Create campaign" over to it.
const CampaignStudio = ComponentLoader(
  lazy(() => import('screens/admin/CampaignStudio'))
)
// TASK-083: employee recognition
const EmployeeRecognitions = ComponentLoader(
  lazy(() => import('screens/admin/Campaigns/recognitions'))
)
// TASK-081: competition leaderboard
const CompetitionLeaderboard = ComponentLoader(
  lazy(() => import('screens/admin/Campaigns/leaderboard'))
)
// TASK-078: attendance sessions and their check-in QR codes
const AttendanceSessions = ComponentLoader(
  lazy(() => import('screens/admin/Campaigns/sessions'))
)

//Reward
const RewardModule = ComponentLoader(lazy(() => import('screens/admin/Reward')))

// GROWTHTOOL
const GrowthToolModule = ComponentLoader(
  lazy(() => import('screens/admin/GrowthTool'))
)
const GrowthTool = ComponentLoader(
  lazy(() => import('screens/admin/GrowthTool/growthtool'))
)

// CUSTOMER
const Customers = ComponentLoader(
  lazy(() => import('screens/admin/Customers/customers'))
)
const Insight = ComponentLoader(
  lazy(() => import('screens/admin/Customers/view-insight'))
)
const Info = ComponentLoader(
  lazy(() => import('screens/admin/Customers/customer-info'))
)
const EditCustomer = ComponentLoader(
  lazy(() => import('screens/admin/Customers/edit-customer'))
)

// SCANNER
const ScannerModule = ComponentLoader(
  lazy(() => import('screens/admin/Scanner/'))
)
const Scanner = ComponentLoader(
  lazy(() => import('screens/admin/Scanner/scanners'))
)
const UsercodeTable = ComponentLoader(
  lazy(() => import('screens/admin/Scanner/usercode-Table'))
)

const Staff = ComponentLoader(lazy(() => import('screens/admin/Scanner/Staff')))
const ScannerLogs = ComponentLoader(
  lazy(() => import('screens/admin/Scanner/logs'))
)
// NOTIFICATION
const BroadCastModule = ComponentLoader(
  lazy(() => import('screens/admin/BroadCast/'))
)
const CreateBroadCast = ComponentLoader(
  lazy(() => import('screens/admin/BroadCast/create'))
)
const UpdateBroadCast = ComponentLoader(
  lazy(() => import('screens/admin/BroadCast/create'))
)
const ViewLogs = ComponentLoader(
  lazy(() => import('screens/admin/BroadCast/view-logs'))
)
const BroadCast = ComponentLoader(
  lazy(() => import('screens/admin/BroadCast/broadcast'))
)
const Notification = ComponentLoader(
  lazy(() => import('screens/admin/BroadCast/index'))
)
const BroadCastLogs = ComponentLoader(
  lazy(() => import('screens/admin/BroadCast/create'))
)
const PushAutomation = ComponentLoader(
  lazy(() => import('screens/admin/BroadCast/push-automation'))
)

// WEBHOOK
const WebhookModule = ComponentLoader(
  lazy(() => import('screens/admin/Webhook/'))
)
const Webhook = ComponentLoader(
  lazy(() => import('screens/admin/Webhook/webhook-list'))
)
const WebhookLogs = ComponentLoader(
  lazy(() => import('screens/admin/Webhook/view-logs'))
)
const CreateWebhook = ComponentLoader(
  lazy(() => import('screens/admin/Webhook/create/create-webhook'))
)
const UpdateWebhook = ComponentLoader(
  lazy(() => import('screens/admin/Webhook/create/create-webhook'))
)

// SETTINGS
const GeneralInfo = ComponentLoader(
  lazy(() => import('screens/admin/GeneralInfo/generalinfo'))
)
const Qrcode = ComponentLoader(
  lazy(() => import('screens/admin/GrowthTool/create/qrcode'))
)
const Formlink = ComponentLoader(
  lazy(() => import('screens/admin/GrowthTool/create/formlink'))
)
const Billings = ComponentLoader(
  lazy(() => import('screens/admin/Billings/billing'))
)

const Plans = ComponentLoader(
  lazy(() => import('screens/admin/SubscriptionPlans/plans'))
)
const Profile = ComponentLoader(
  lazy(() => import('screens/admin/Profile/profile'))
)

const GeoLocationModule = ComponentLoader(
  lazy(() => import('screens/admin/GeoLocation'))
)
const ApiKey = ComponentLoader(
  lazy(() => import('screens/admin/ApiKey/apikey'))
)
const IntegrateStripe = ComponentLoader(
  lazy(() => import('screens/public/stripe/stripe'))
)

// Roles

const RolesModule = ComponentLoader(
  lazy(() => import('screens/admin/Roles'))
)
const CreateRoles = ComponentLoader(
  lazy(() => import('screens/admin/Roles/create'))
)
const UpdateRoles = ComponentLoader(
  lazy(() => import('screens/admin/Roles/create'))
)
const Roles = ComponentLoader(
  lazy(() => import('screens/admin/Roles/roles'))
)
// Team

const TeamModule = ComponentLoader(
  lazy(() => import('screens/admin/Team'))
)
const CreateTeam = ComponentLoader(
  lazy(() => import('screens/admin/Team/create'))
)
const UpdateTeam = ComponentLoader(
  lazy(() => import('screens/admin/Team/create'))
)
const Team = ComponentLoader(
  lazy(() => import('screens/admin/Team/team'))
)

const AdminRoutes = () => {
  const { isAccessable } = useLocationInfo()
  const { currentUser } = useAuth()
  const combinedPermissions = currentUser?.roles?.reduce((acc, role) => {
    Object.entries(role).forEach(([key, value]) => {
      if (typeof value === 'object' && value !== null) {
        acc[key] = acc[key] || {};
        Object.entries(value).forEach(([permKey, permValue]) => {
          acc[key][permKey] = acc[key][permKey] || permValue;
        });
      }
    });
    return acc;
  }, {}) || {};

  const hasPermission = (key) => {
    const section = combinedPermissions[key];
    if (!section) return false;
    return (
      section.read ||
      section.access ||
      section.create ||
      section.update ||
      section.delete
    );
  };

  const mainRoutes = [
    hasPermission("dashboard") && { path: "dashboard" },
    hasPermission("campaigns") && isAccessable("campaign") && { path: "campaigns" },
    hasPermission("customers") && isAccessable("customer") && { path: "customers" },
    hasPermission("broadcast") && isAccessable("broadcast") && { path: "broadcast" },
    hasPermission("roles") && { path: "roles" },
    hasPermission("team") && { path: "team" },
    hasPermission("scanner") && isAccessable("scanner") && { path: "scanner" },
  ].filter(Boolean);

  const defaultPath = mainRoutes[0]?.path || "profile";

  console.log("Redirecting to:", defaultPath);

  console.log('CAMAPIGN', isAccessable('campaign'))
  return [
    {
      path: '',
      element: <AdminLayout />,
      children: [
        hasPermission('dashboard') && {
          path: 'dashboard',
          element: <Dashboard />,
        },

        hasPermission('campaigns') && isAccessable('campaign')
          ? {
            path: 'campaigns',
            element: <CampaignModule />,
            children: [
              { path: '', index: true, element: <CampaignList /> },
              // The old campaign form was replaced by the studio (2026-10-20).
              { path: 'create', element: <Navigate to='/campaigns/studio' replace /> },
              { path: 'update', element: <Navigate to='/campaigns' replace /> },
              { path: 'insight', element: <CampaignInsight /> },
              { path: 'studio', element: <CampaignStudio /> },
              { path: 'studio/:id', element: <CampaignStudio /> },
              { path: 'sessions/:id', element: <AttendanceSessions /> },
              { path: 'leaderboard/:id', element: <CompetitionLeaderboard /> },
              { path: 'recognitions/:id', element: <EmployeeRecognitions /> },
            ],
          }
          : null,

        hasPermission('customers') && isAccessable('customer')
          ? {
            path: 'customers',
            children: [
              { path: '', index: true, element: <Customers /> },
              { path: 'view', element: <Insight /> },
              { path: 'info', element: <Info /> },
              { path: 'edit', element: <EditCustomer /> },
            ],
          }
          : null,

        hasPermission('broadcast') && isAccessable('broadcast')
          ? {
            path: 'broadcast',
            element: <BroadCastModule />,
            children: [
              { path: '', index: true, element: <BroadCast /> },
              { path: 'create-broadcast', element: <CreateBroadCast /> },
              { path: 'update', element: <UpdateBroadCast /> },
              { path: 'view-logs', element: <ViewLogs /> },
            ],
          }
          : null,

        hasPermission('roles')
          ? {
            path: 'roles',
            element: <RolesModule />,
            children: [
              { path: '', index: true, element: <Roles /> },
              { path: 'create-roles', element: <CreateRoles /> },
              { path: 'update', element: <UpdateRoles /> },
            ],
          }
          : null,

        hasPermission('team')
          ? {
            path: 'team',
            element: <TeamModule />,
            children: [
              { path: '', index: true, element: <Team /> },
              { path: 'create-team', element: <CreateTeam /> },
              { path: 'update', element: <UpdateTeam /> },
            ],
          }
          : null,

        hasPermission('scanner') && isAccessable('scanner')
          ? {
            path: 'scanner',
            element: <ScannerModule />,
            children: [
              { path: '', index: true, element: <Scanner /> },
              { path: 'staff', element: <Staff /> },
              { path: 'logs', element: <ScannerLogs /> },
              { path: 'usercode-table', element: <UsercodeTable /> },
            ],
          }
          : null,

        hasPermission('webhook')
          ? {
            path: 'webhook',
            element: <WebhookModule />,
            children: [
              { path: '', index: true, element: <Webhook /> },
              { path: 'create-webhook', element: <CreateWebhook /> },
              { path: 'update', element: <UpdateWebhook /> },
              { path: 'view-logs', element: <WebhookLogs /> },
            ],
          }
          : null,

        {
          path: 'profile',
          element: <Profile />,
        },

        {
          path: 'settings',
          children: [
            { path: 'generalinfo', element: <GeneralInfo /> },
            { path: 'billings', element: <Billings /> },
            { path: 'plans', element: <Plans /> },
            { path: 'apikey', element: <ApiKey /> },
            { path: '', index: true, element: <Navigate replace to={'./generalinfo'} /> },
            { path: '*', element: <Navigate replace to={'./generalinfo'} /> },
          ],
        },

        { path: '', index: true, element: <Navigate replace to={`./${defaultPath}`} /> },
        { path: '*', index: true, element: <Navigate replace to={`./${defaultPath}`} /> },
      ].filter(Boolean),
    },

    { path: 'home', element: <Home /> },
    { path: "", index: true, element: <Navigate replace to={`./${defaultPath}`} /> },
    { path: "*", element: <Navigate replace to={`./${defaultPath}`} /> },
    {
      path: 'integrations',
      children: [{ path: 'stripe', element: <IntegrateStripe /> }],
    },
  ]
}

export default AdminRoutes
